"use strict"
var inherits = require('util').inherits
var _ = require('lodash')
var Counter = require('./Counter')
var ipAddressUtil = require('pw-ipaddr.js')

var DEFAULT_SORT = 'bytesDesc'

var DEFAULT_VIEW_SETTINGS = {
  sortMethod: DEFAULT_SORT, // set data sort method and direction
  rangeFilter: null, //[0, 100], // filter by passed percentage-range (e.g. [0,60]) of ordered bandwidth data
  modeFilter: 'all', // (all|packet|head|netflow|none) filter by passed mode string (fp/sh/nf/dp)
  estimateCapture: false,  // (bool, Protocol mode only) true=estimate capture based on selected captureProfile
  collapseSingles: true, // (bool, IP mode only) prevent single-ip children in nested subnet tree (bubble to the top instead)
  flattenList: false, // (bool, IP mode only) prevent recursive subnet nesting

  averageCompressionRatio: 0.7,
  defaultMode: 'packet',
  exceptions: null
}

var VALID_VIEW_SETTINGS = Object.keys(DEFAULT_VIEW_SETTINGS)

function getValidViewSettings (viewOptions) {
  return _.pick(viewOptions, VALID_VIEW_SETTINGS)
}

var SORT_METHODS = {
  bytesDesc: function (itm) { return -itm.bytes },
  bytesAsc: function (itm) { return itm.bytes },
  nameDesc: function (itm) { return -itm.name.charCodeAt() },
  nameAsc: function (itm) { return itm.name }
}

var _currentSortMethod = SORT_METHODS[DEFAULT_SORT]  //.bytesDesc
var _unassignedExceptions = null

function _attachExceptions (exceptionVal, defaultMode) {
  // Attach matching exceptions
  var output = {
    mode: defaultMode,
    exception_id: null
  }
  if (_unassignedExceptions) {
    var exc = _.find(_unassignedExceptions, {'ip_address': exceptionVal})
    if (exc) {
      output = {
        mode: exc.mode,
        exception_id: exc.id
      }
      _.pull(_unassignedExceptions, exc) // Remove this exception from the remaining un-assigned exceptions
    }
  }
  return output
}

function _getCidr (depth, isIPv6) {
  if (isIPv6) {
    switch (depth) {
      case 2:
        return [ ":0:0:0:0:0:0:0", 16 ]
      case 3:
        return [ ":0:0:0:0:0:0", 32 ]
      case 4:
        return [ ":0:0:0:0:0", 48 ]
      case 5:
        return [ ":0:0:0:0", 64 ]
      case 6:
        return [ ":0:0:0", 80 ]
      case 7:
        return [ ":0:0", 96 ]
      case 8:
        return [ ":0", 112 ]
      default:
        return [ "", 128 ] // single IP
    }
  } else {
    switch (depth) {
      case 2:
        return [ ".0.0.0", 8 ]
      case 3:
        return [ ".0.0", 16 ]
      case 4:
        return [ ".0", 24 ]
      default:
        return [ "", 32 ] // single IP
    }
  }
}

// var _protocolFamilyColors = constants.protocolFamilyColors
var _protocolFamilyColors = null
var _numColors = 0
var _allTopLevelSubnets = [] // Filled by root _getGroup call
var _allTopLevelIpsGrouped = {}
var _colorIdxMap = {}
var _ipBatch = null
var _latestViewSettings = null


// Recursive grouping of ip's into subnets.
// Params:
// - parentMode: any "owner" CIDR
function _getGroup (groupedSrc, depth, parentColorIdx, parentMode, topSubnet) {

  // Traverse children for each passed group component
  return _.reduce(groupedSrc, function (groupedOutput, subnetIPs, subnet) {
    var _isIPv6 = false
    if (depth >= 2 && subnetIPs.length > 0) {
      _isIPv6 = _ipBatch.isIpv6(subnetIPs[ 0 ]._id)
      // _isIPv6 = _ipBatch.parseIp(subnetIPs[ 0 ]._id )?.kind() == 'ipv6' # Test if this group is IPv6
    }
    var _maxGroupDepth = _isIPv6 ? 7 : 3 //8 & 4 respective groups for ipv4 and ipv6
    var _isFinalDepth = depth > _maxGroupDepth
    var _colorIdx = parentColorIdx
    var _topSubnet = topSubnet
    var _cidr = _getCidr(depth, _isIPv6)
    // _name = if _isIPv6 then _ipBatch.parseIp("#{ subnet }#{ _cidr[ 0 ] }" )?.toString() else subnet
    var _name = _isIPv6 ? _ipBatch.getParsedString(subnet + _cidr[0] + "") : subnet
    var _exceptionVal = _isIPv6 ? (_name + "/" + _cidr[1]) : (_name + _cidr.join('/'))
    var _exception = _attachExceptions(_exceptionVal, parentMode)
    var _groupedChildren = []

    // Compute top-level color for highest subnets, will be inherited by all descendants
    if (depth === 2) {
      _topSubnet = subnet
      if (_colorIdxMap[subnet] && _colorIdxMap[subnet] != null) {
        _colorIdx = _colorIdxMap[subnet] // Use previously assigned color
      } else {
        // No color yet
        _colorIdx = _allTopLevelSubnets.indexOf(subnet)
        if (_colorIdx >= _numColors) {
          _colorIdx = _colorIdx - (_numColors * Math.floor(_colorIdx / _numColors)) // Wrap to beginning of colors
        }
        _colorIdxMap[subnet] = _colorIdx
      }
    }

    if (_isFinalDepth) {
      // Final depth achieved, apply inherited values to children
      _groupedChildren = _.reduce(subnetIPs, function (childrenOut, child) {
        var _childExc = _attachExceptions(child._id, _exception.mode)
        var _isVisible = _latestViewSettings.modeFilter === 'all' || _latestViewSettings.modeFilter === _childExc.mode
        child = _.assign(child, {
          mode: _childExc.mode,
          exception_id: _childExc.exception_id,
          color: _protocolFamilyColors[parentColorIdx],
          colorIdx: parentColorIdx,
          topSubnet: _latestViewSettings.flattenList ? child._id.replace(/\./g, '_') : _topSubnet, // Flat list items refer to selves as top subnet
          ipv6: _isIPv6,
          isVisible: _isVisible
        })
        // Attach child only if unfiltered
        if (_isVisible) {
          childrenOut.push(child)
          if (_latestViewSettings.flattenList) {
            _colorIdxMap[child._id.replace(/\./g, '_')] = parentColorIdx // Flattened list needs colors keyed by IPs (at final depth) for timeline
          }
        }
        return childrenOut
      }, [])
    } else {
      // Continue to descend the tree
      var _currGrouped = _.groupBy(subnetIPs, function (itm) {
        // itm.parsedIp ?= _ipBatch.parseIp(itm._id)
        var ip = itm._id
        var _ipComponents = _ipBatch.getComponents(ip)
        var ipv6 = _ipComponents.ipv6
        var components = _ipComponents.components

        //if itm.parsedIp.octets and itm.parsedIp.octets.length #ipv4
        if (ipv6) {
          // return itm.parsedIp.toNormalizedString().split(':').slice(0, depth).join(':')
          return _ipBatch.getParsedString(ip, true).split(':').slice(0, depth).join(':')
        } else {
          //if itm.parsedIp.parts and itm.parsedIp.parts.length #ipv6: Parse and convert to most condensed string representation, then split by colons
          return components.slice(0, depth).join('.')
        }
      })
      if (depth === 1) {
        _allTopLevelSubnets = _.keys(_currGrouped) // Store for later color computation, sort all subnets in repeatable ascending order
        _allTopLevelIpsGrouped = _.mapValues(_currGrouped, function (subnetChildren) {
          return _.pluck(subnetChildren , '_id') //_.pluck(_.values(_currGrouped ) , '_id') //Mapping of top subnets to child IPs only
        })
      }
      _groupedChildren = _getGroup(_currGrouped, depth + 1, _colorIdx, _exception.mode, _topSubnet) // group the next subnet level down
    }

    var _total = _.reduce(_groupedChildren, function (total, itm) {
      total += itm.bytes
      return total
    }, 0)

    // Filter out any subnets that possess zero children,
    // unless it's the root (which should always be passed through)
    if (_.size(_groupedChildren) > 0 || depth <= 1) {
      // If this subnet has a single child _and_ has no associated exception,
      // we can eliminate it and bump the child up a level
      if (_latestViewSettings.collapseSingles
        && _.size(_groupedChildren) === 1
        && _exception.exception_id == null
        && subnet !== 'root') {
        // Collapse this single child vertically into the parent grouping
        if (_.isArray(_groupedChildren)) {
          groupedOutput[_groupedChildren[0]._id] = _groupedChildren[0] // Bottom level IP
        } else {
          var _onlyKey = _.keys(_groupedChildren)[0]
          groupedOutput[_onlyKey] = _groupedChildren[_onlyKey] // Intermediate Subnet
        }
      } else {
        groupedOutput[subnet] = {
          _id: subnet,
          name: _name,
          cidr: _cidr,
          exceptionVal: _exceptionVal,
          // level: depth,
          children: _.sortBy(_groupedChildren, _currentSortMethod),
          bytes: _total,
          count: _.size(_groupedChildren),
          end: false,
          isIp: true,
          color: _protocolFamilyColors[_colorIdx],
          colorIdx: _colorIdx,
          mode: _exception.mode,
          exception_id: _exception.exception_id,
          topSubnet: _topSubnet,
          ipv6: _isIPv6
        }
      }
    }
    return groupedOutput
  }, {})
}



function dataTransform (data) {
  var history = data.history
  var historyTotal = 0

  _currentSortMethod = SORT_METHODS[_latestViewSettings.sortMethod] || SORT_METHODS[DEFAULT_SORT]

  _ipBatch = ipAddressUtil.batch() // New IP batch for this dataTransform call

  // Copy excetions list into a new collection that will be mutated as exceptions are matched to relevant history data items (ips/subnets)
  // Used to render any remaining IP exceptions that may not match anything in the current data set. Must be "refreshed" on each data tick
  _unassignedExceptions = _.filter(_.cloneDeep(_latestViewSettings.exceptions), 'ip_address')

  // Sorted & totaled list of all ips in the range
  var ipTotals = _.reduce(history, function (out, tick) {
    _.forIn(tick, function (val, key) {
      if (key === 'total') {
        historyTotal += val
      } else if (_ipBatch.isValidIp(key)) {
        // Add to IP total if exists in output, otherwise create new entry
        if (out[key]) {
          out[key] += val
        } else {
          out[key] = val
        }
      }
    })
    return out
  }, {})

  var sortedIps = _(ipTotals)
    .map(function (val, key) {
      return {
        _id: key,
        name: key,
        exceptionVal: key,
        bytes: val,
        end: true,
        isIp: true
      }
    })
    .sortBy(SORT_METHODS.bytesAsc) // Byte sort first for range filtering
    // .sortBy(_currentSortMethod)
    .value()

  var _numIps = sortedIps.length
  var _cullLimit = _latestViewSettings.flattenList ? 100 : 500 // Flat list becomes slow above 100 items
  // IP Only: choose a sensible default filter to reduce slowdown for customer sensots that may have 2k+ top IPs
  if (_numIps > _cullLimit && _latestViewSettings.rangeFilter == null && _latestViewSettings.modeFilter === 'all') {
    var _cullPerc = Math.round(100 / (_numIps / (_numIps - _cullLimit)))
    _latestViewSettings.rangeFilter = [_cullPerc , 100]
  }
  if (_latestViewSettings.rangeFilter != null && !_.isEqual(_latestViewSettings.rangeFilter, [0, 100])) {
    var startIndex = Math.floor(_numIps * (_latestViewSettings.rangeFilter[0] / 100))
    var endIndex = Math.ceil(_numIps * (_latestViewSettings.rangeFilter[1] / 100))
    sortedIps = sortedIps.slice(startIndex, endIndex)
  }

  sortedIps = _.sortBy(sortedIps, _currentSortMethod) // Now sort by user-defined list method
  var sortedIpKeys = _.map(sortedIps, function (itm) { return itm._id })

  var groupedIps = _getGroup({'root': sortedIps}, 1, 0, _latestViewSettings.defaultMode)

  // Use grouped data to draw simplified timeline (unless flattened)
  var _maxValue = data.maxValue
  var _history = history
  var _timelineKeys = sortedIpKeys

  if (_latestViewSettings.flattenList) {
    groupedIps.root.children = _.filter(sortedIps, 'isVisible') // Replace nested list with flattened IPs (still colored by parent subnets)
  } else {
    _history = _.reduce(history, function (output, tick) {
      var _tickTotal = 0
      var _tick = {}
      // for topLevel, i in groupedIps.root.children
      for (var i = 0, iLen = groupedIps.root.children.length; i < iLen; i++) {
        var topLevel = groupedIps.root.children[i]
        var _subnetTotal = _.reduce(_allTopLevelIpsGrouped[topLevel.topSubnet], function (_total, ip) {
          var _t = tick[ip]
          return _t ? _t + _total : _total
        }, 0)
        _tickTotal += _subnetTotal
        _tick[topLevel.topSubnet] = _subnetTotal
      }
      _tick.total = _tickTotal
      _tick.timestamp = tick.timestamp
      _maxValue = _tickTotal > _maxValue ? _tickTotal : _maxValue
      output.push(_tick)
      return output
    }, [])
    _timelineKeys = _.pluck(groupedIps.root.children, 'topSubnet').reverse()
  }

  _ipBatch = null // Release batched IPs for GC

  data.packedData = groupedIps.root
  data.unassignedExceptions = _unassignedExceptions
  data.history = _history // stack data for timeline, filtered to only include keys with values across the time span. May be live or estimated based on mode
  data.sortedIps = sortedIps
  data.totalBytes = historyTotal
  data.sortedKeys = _timelineKeys //_.pluck(groupedIps.root.children, 'topSubnet').reverse() #Despite single-child collapsing, colors are assigned by top-level subnet
  data.colorIdxMap = _colorIdxMap
  data.maxValue = _maxValue
  return _.assign(data, _latestViewSettings)
}


/**
 * Graph Constructor / setup
 */
function ProfilerIpGraph (graphOptions, viewOptions) {
  if (!(this instanceof ProfilerIpGraph)) {
    return new ProfilerIpGraph(graphOptions, viewOptions)
  }
  // this._protocolFamilies = _.reject(viewOptions.protocolFamilies, function (family) {
  //   return family.protocols.length === 0
  // })
  // this._protocols = viewOptions.protocols
  // this._protocolFamilyIdToColor = viewOptions.protocolFamilyIdToColor
  _protocolFamilyColors = viewOptions.protocolFamilyColors
  _numColors = _protocolFamilyColors.length

  _latestViewSettings = _.defaults(getValidViewSettings(viewOptions), DEFAULT_VIEW_SETTINGS)

  // Initialize inherited Counter graph functions
  Counter.call(this, graphOptions, viewOptions)
}

inherits(ProfilerIpGraph, Counter)

var proto = ProfilerIpGraph.prototype

proto.getGraphData = function () {
  var counterGraphData = Counter.prototype.getGraphData.call(this)
  return dataTransform.call(this, counterGraphData)
}

proto.setViewOptions = function (partialViewOptions) {
  // Merge in the new partial view options
  _latestViewSettings = _.assign(_latestViewSettings, getValidViewSettings(partialViewOptions))
}

module.exports = ProfilerIpGraph
